/**
 * The public landing for a "Share" link (see HistoryView's Share button and
 * backend routes_validate.py's /api/shared/{token}). No login, no API key,
 * no masthead nav -- this is meant to work for someone the report owner
 * just emailed a link to, who has never used ClearIFC and never will.
 */

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { ValidationReport } from '../lib/types';
import { ReportView } from './ReportView';
import { Callout, Logo, Spinner } from './primitives';

export function SharedReportView({ shareToken }: { shareToken: string }) {
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.getSharedReport(shareToken);
        if (!cancelled) setReport(body.report);
      } catch (caught) {
        if (!cancelled) {
          setError(
            caught instanceof ApiError && caught.status === 404
              ? "This link doesn't work any more — it may have been revoked, or never existed."
              : caught instanceof ApiError
                ? caught.message
                : 'Could not load this report.',
          );
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareToken]);

  return (
    <div className="app">
      <header className="masthead">
        <div className="masthead-inner">
          <div className="masthead-top">
            <a className="logo" href="https://clearifc.com/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Logo />
              <div>
                ClearIFC
                <div className="logo-sub">Shared report</div>
              </div>
            </a>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container stack">
          {busy && (
            <div className="row">
              <Spinner /> <span className="hint-strong">Loading report…</span>
            </div>
          )}

          {error && <Callout variant="error">{error}</Callout>}

          {report && (
            <>
              <Callout>
                Shared read-only view — the model itself was never stored; this is the
                validation report only.
              </Callout>
              <ReportView report={report} />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
